import React, { useEffect, useState } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import CustomButton from "../../components/CustomButton";
import { icons } from "../../assets/icons";
import { useNavigate, useLocation } from "react-router-dom";
import CustomTable from "../../components/CustomTable";
import DatePicker from "../../components/FormFields/DatePicker";
import moment from "moment";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import DetailsCard from "../../components/DetailsCard";
import { FaChevronRight } from "react-icons/fa";
import PreviewDocModal from "./PreviewDocModal";
import { useAuth } from "../../contexts/AuthContext";
import { useMediaQuery } from "react-responsive";
import { getArrayOfSelectedStoresCode } from "../../../srcWeb/utils/helpers";
import { normalizeCashDepositListResponse } from "../../../srcWeb/utils/dmsStandaloneListUtils";

const CashDeposits = () => {
  const isMobileSmall = useMediaQuery({ query: "(max-width: 344px)" });
  const { data = [], apiCall } = useApi();
  const { apiCall: fileDownload } = useApi();
  const { user = {}, storeDropdownState } = useAuth();

  const storeLogin = user?.userLabel == "Store";
  const navigate = useNavigate();
  const location = useLocation();
  const [startDate, setStartDate] = useState(
    moment().subtract({ days: 7 }).toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [downloadPreviewOpenWithData, setDownloadPreviewOpenWithData] =
    useState(false);

  const onClickSubmitCashDeposit = () => {
    navigate("/cash-deposit/submit");
  };

  const { selectedStores } = useAuth();

  const cashDepositApi = (start, end) => {
    apiCall({
      url: apiUrls.dmsService.getRecords,
      dataToSend: {
        startDate: moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
        metadataSchemaId: 7,
        endDate: moment(end || start)
          .endOf("d")
          .format("YYYY-MM-DD HH:mm:ss"),
        searchText: "",
        status: null,
        storeCodes: getArrayOfSelectedStoresCode(
          selectedStores,
          storeDropdownState
        ),
      },
      method: "post",
      processData: (raw) => normalizeCashDepositListResponse(raw),
    });
  };

  useEffect(() => {
    cashDepositApi(startDate, endDate);
  }, [selectedStores, storeDropdownState, location.key]);

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end !== null) cashDepositApi(start, end);
  };

  const onClickDownloadFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        const url = window.URL.createObjectURL(new Blob([apiData.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const onClickViewFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
          file_id: file_id,
        });
      },
    });
  };

  const columns = [
    {
      Header: "Date",
      accessor: "sales_day",
      Cell: (props) => {
        const value = props?.sales_day;
        return <div className="">{value}</div>;
      },
    },
    {
      Header: "Description",
      accessor: "comment",
    },
    {
      Header: "Total Sales",
      accessor: "total_cash_sales",
      Cell: (props) => {
        const value = props?.total_cash_sales;
        return value ? Number(value).toFixed(2) : "-";
      },
    },
    {
      Header: "Cash Deposit",
      accessor: "cash_deposit",
      Cell: (props) => {
        const value = props?.cash_deposit;
        return value ? Number(value).toFixed(2) : "-";
      },
    },
    {
      Header: "Attachment",
      accessor: "id",
      Cell: (rowData) => {
        return (
          <div className="flex items-center gap-x-5">
            <div
              className={`cursor-pointer text-[#346BFF] underline ${
                isMobileSmall ? "text-[1.4rem]" : "text-[2rem]"
              }`}
              onClick={() =>
                onClickViewFile(rowData?.file_id, rowData?.filename)
              }
            >
              View
            </div>
            <div
              className={`cursor-pointer text-[#346BFF] underline ${
                isMobileSmall ? "text-[1.4rem]" : "text-[2rem]"
              }`}
              onClick={() =>
                onClickDownloadFile(rowData?.file_id, rowData?.filename)
              }
            >
              Download
            </div>
          </div>
        );
      },
    },
  ];

  const cashDepositsData = Array.isArray(data) ? data : [];

  return (
    <div className="dashboard-container">
      <ToolbarHeader
        title="Cash Deposit"
        showBack={false}
        containerClassName="mt-14 mb-4"
        textClassCss={isMobileSmall ? "text-[2.5rem]" : "text-[3rem]"}
        renderRight={() => {
          if (user?.userLabel === "Store")
            return (
              <CustomButton
                title="Submit Cash Deposit"
                icon={icons.plusIcon}
                textClassName={
                  isMobileSmall ? "text-[1.4rem]" : "text-[1.5rem]"
                }
                onClick={onClickSubmitCashDeposit}
              />
            );
        }}
      />

      <DatePicker
        startDate={startDate}
        endDate={endDate}
        onChange={onChangeDate}
        maxDate={new Date()}
        dateRange
      />

      <div className="mt-3 overflow-auto" style={{ maxHeight: "93vh" }}>
        <DetailsCard
          rows={columns}
          data={cashDepositsData}
          customRow={(row, rowData) => {
            if (row.accessor === "sales_day") {
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                >
                  <div className="min-w-[18rem] font-size-medium">
                    {!storeLogin ? rowData?.storeCode || "" : ""}
                  </div>

                  <FaChevronRight color="var(--whiteColor)" size={15} />
                </div>
              );
            }
          }}
        />
      </div>
      <PreviewDocModal
        isModalVisible={downloadPreviewOpenWithData}
        setIsModalVisible={setDownloadPreviewOpenWithData}
      />
    </div>
  );
};

export default CashDeposits;
